/**
 * Service centralisé pour la gestion des assets Directus
 * Gère les URLs des fichiers (avatars, logos, etc.)
 */

/**
 * Options pour la transformation d'image
 */
export interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number; // 1-100
  fit?: "cover" | "contain" | "inside" | "outside";
  format?: "jpg" | "png" | "webp" | "avif";
}

/**
 * Génère l'URL ABSOLUE d'un fichier Directus (`${assetsBaseUrl}/api/assets/<id>`).
 *
 * ⚠️ Avant : générait `/api/assets/<id>` (chemin relatif vers un proxy Nuxt
 * `/api/assets` qui N'EXISTE PAS dans donoor.org) → les images tombaient en 404
 * sur le front (ex. bannière de page de paiement). On pointe désormais
 * directement sur Directus, comme `organizationLogoUrl` (qui fonctionne).
 *
 * @param fileId - L'ID du fichier Directus ou une URL complète
 * @param options - Options de transformation d'image (optionnel)
 * @returns L'URL absolue du fichier sur Directus
 */
export function getFileUrl(
  fileId: string | null | undefined,
  options?: ImageTransformOptions
): string {
  if (!fileId) return "";

  let assetId: string | null = null;

  // Si c'est déjà une URL complète, extraire l'ID
  if (fileId.startsWith("http")) {
    // Pattern pour extraire l'ID depuis différentes formes d'URLs Directus:
    // - https://directus.donoor.org/assets/xxx
    // - http://localhost:8055/assets/xxx
    // - https://directus.donoor.org/api/assets/xxx (si jamais)
    const match = fileId.match(/\/assets\/([a-f0-9-]{36}|[a-f0-9-]+)/i);
    if (match && match[1]) {
      assetId = match[1];
    } else {
      // Si on ne peut pas extraire l'ID, retourner l'URL telle quelle (fallback)
      return fileId;
    }
  } else {
    assetId = fileId;
  }

  // Route ASSETS DU BACKEND, pas Directus en direct.
  //
  // Directus n'autorise le rôle public qu'à lire une liste blanche de
  // dossiers : un fichier rangé ailleurs (ou pas rangé du tout) renvoie 403 et
  // l'image s'affiche cassée. La route backend, elle, bascule sur le token
  // admin côté serveur ET refuse les dossiers sensibles — c'est le seul chemin
  // à la fois fiable et sûr (Karim 2026-09-09).
  const config = useRuntimeConfig();
  const apiUrl = String(
    config.public.apiUrl || "https://api.donoor.org",
  ).replace(/\/$/, "");
  let url = `${apiUrl}/api/assets/${assetId}`;

  if (options) {
    const params = new URLSearchParams();
    if (options.width) params.set("width", String(options.width));
    if (options.height) params.set("height", String(options.height));
    if (options.quality) params.set("quality", String(options.quality));
    if (options.fit) params.set("fit", options.fit);
    if (options.format) params.set("format", options.format);

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  return url;
}

/**
 * Génère l'URL d'un fichier en haute qualité (pour QR codes, affichage plein écran, etc.)
 * Note: Pour les QR codes, préférer getQrCodeHighResUrl() qui utilise l'endpoint de téléchargement
 * @param fileId - L'ID du fichier Directus ou une URL complète
 * @returns L'URL du fichier en haute qualité via le proxy Nuxt
 */
export function getHighQualityFileUrl(fileId: string | null | undefined): string {
  return getFileUrl(fileId, { quality: 100 });
}

/**
 * Génère l'URL d'un QR code en haute résolution via l'endpoint de téléchargement
 * Régénère le QR code à la taille demandée côté serveur (pas d'upscaling)
 * @param qrCodeId - L'ID du QR code
 * @param size - Taille en pixels: 512, 1024, 2048, 4096 (défaut: 2048)
 * @param format - Format: 'png' ou 'svg' (défaut: 'png')
 * @returns L'URL de téléchargement haute résolution
 */
export function getQrCodeHighResUrl(
  qrCodeId: string | null | undefined,
  size: number = 2048,
  format: "png" | "svg" = "png"
): string {
  if (!qrCodeId) return "";
  const config = useRuntimeConfig();
  const baseUrl = config.public.apiUrl || "";
  return `${baseUrl}/api/qr-codes/${qrCodeId}/download?format=${format}&size=${size}`;
}
